import './styles/global.css';
import './styles/modes.css';
import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { I18nProvider, useI18n, getContent } from './i18n';
import { useRoute, Link, buildPath, buildLeaderboard, buildPrivacy, buildTerms, buildCountries, type ModeKey, type View } from './router';
import { applySeo } from './lib/seo';
import { pageview } from './lib/analytics';
import { localName, flagSrc, byCca3, type Country, type Lang } from './lib/countries';
import { MODES, answerFor, solvedModesToday } from './lib/daily';
import { gameNumber, todayKey, prevDay } from './lib/game';
import { getStreak } from './lib/storage';
import { reveal } from './lib/api';
import { IconGlobe, IconFlag, IconOutline, IconCheck, IconConnections, IconDraw, IconTrivia } from './components/Icons';
import LangSwitcher from './components/LangSwitcher';
import AccountChip from './components/AccountChip';
import { AccountProvider } from './account';
import ModeToggle from './components/ModeToggle';
import ReportShare from './components/ReportShare';
import Countdown from './components/Countdown';
import EndlessBar from './components/EndlessBar';
import SeoContent from './components/SeoContent';
import RecommendedGames from './components/RecommendedGames';
import NativeBanner from './components/NativeBanner';
import CountryLink from './components/CountryLink';
import InView from './components/InView';
import { LeaderboardSkeleton, ProfileSkeleton } from './components/Skeleton';
import ClassicMode from './modes/ClassicMode';
import FlagMode from './modes/FlagMode';
import OutlineMode from './modes/OutlineMode';
import ConnectionsMode from './modes/ConnectionsMode';
import DrawMode from './modes/DrawMode';
import TriviaMode from './modes/TriviaMode';
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Settings = lazy(() => import('./pages/Settings'));
const CountryPage = lazy(() => import('./pages/CountryPage'));
const CountriesPage = lazy(() => import('./pages/CountriesPage'));

const MODE_DEFS: { key: ModeKey; Icon: typeof IconGlobe }[] = [
  { key: 'classic', Icon: IconGlobe },
  { key: 'flag', Icon: IconFlag },
  { key: 'outline', Icon: IconOutline },
  { key: 'connections', Icon: IconConnections },
  { key: 'draw', Icon: IconDraw },
  { key: 'trivia', Icon: IconTrivia },
];

function DailyStrip({
  lang,
  mode,
  solved,
  streak,
}: {
  lang: Lang;
  mode: ModeKey;
  solved: string[];
  streak: number;
}) {
  const { t } = useI18n();
  const day = todayKey();
  // Flag/Outline 昨日答案前端可算;Classic 竞技答案在服务端,改用 /api/reveal。
  const [yest, setYest] = useState<Country>(() => answerFor(mode, prevDay(day)));
  useEffect(() => {
    let on = true;
    setYest(answerFor(mode, prevDay(day)));
    if (mode === 'classic') {
      reveal(prevDay(day))
        .then((r) => {
          const c = byCca3(r.cca3);
          if (on && c) setYest(c);
        })
        .catch(() => {});
    }
    return () => {
      on = false;
    };
  }, [mode, day]);
  return (
    <div className="daily-strip">
      <div className="ledger daily-ledger">
        <div className="ds-item">
          <span className="ds-k">{t.streak}</span>
          <span className="ds-v">{streak}</span>
        </div>
        <div className="ds-item">
          <span className="ds-k">{t.today}</span>
          <span className="ds-pips">
            {MODES.map((m) => (
              <i key={m} className={solved.includes(m) ? 'on' : ''} />
            ))}
          </span>
        </div>
        <div className="ds-item">
          <span className="ds-v">#{gameNumber(day)}</span>
        </div>
        <div className="ds-item ds-yest">
          <span className="ds-k">{t.yesterday}</span>
          <CountryLink country={yest} lang={lang} className="ds-country-link">
            <img className="ds-flag" src={flagSrc(yest)} alt="" loading="lazy" />
            <span className="ds-name">{localName(yest, lang)}</span>
          </CountryLink>
        </div>
        <div className="ds-item ds-share-item">
          <ReportShare num={gameNumber(day)} solved={solved} streak={streak} lang={lang} />
        </div>
      </div>
    </div>
  );
}

function Shell({
  lang,
  mode,
  endless,
  view,
  userId,
  slug,
}: {
  lang: Lang;
  mode: ModeKey;
  endless: boolean;
  view: View;
  userId?: string;
  slug?: string;
}) {
  const { t } = useI18n();
  const meta = getContent(lang).meta[mode];
  const [solved, setSolved] = useState<string[]>(() => solvedModesToday());
  const [streak, setStreak] = useState<number>(() => getStreak());

  useEffect(() => {
    const refresh = () => {
      setSolved(solvedModesToday());
      setStreak(getStreak());
    };
    window.addEventListener('geodle:progress', refresh);
    return () => window.removeEventListener('geodle:progress', refresh);
  }, []);

  useEffect(() => {
    if (view === 'game') applySeo(lang, mode, endless);
    else if (view !== 'country' && view !== 'countries') {
      // country/countries 页由页面组件自行设置 title/desc/canonical(保留预渲染 SEO)
      const ttl =
        view === 'leaderboard'
          ? t.lb.title
          : view === 'privacy'
            ? t.privacy.title
            : view === 'terms'
              ? t.terms.title
              : view === 'settings'
                ? t.settings.title
                : 'Explorer';
      document.title = `${ttl} | GeoDle`;
    }
    // 个人/设置页 noindex(不入索引);其余可索引。
    let robots = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', view === 'profile' || view === 'settings' ? 'noindex, nofollow' : 'index, follow');
  }, [lang, mode, endless, view, t]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    pageview(view === 'game' ? buildPath(lang, mode, endless) : location.pathname);
  }, [lang, mode, endless, view]);

  // 切换 Daily ⇆ Unlimited 时,游戏面板轻微淡入+上移(仅切换后触发,首屏加载不触发)
  const [switching, setSwitching] = useState(false);
  const prevEndless = useRef(endless);
  useEffect(() => {
    if (prevEndless.current !== endless) {
      prevEndless.current = endless;
      setSwitching(true);
    }
  }, [endless]);

  const ModeComp =
    mode === 'flag'
      ? FlagMode
      : mode === 'outline'
        ? OutlineMode
        : mode === 'connections'
          ? ConnectionsMode
          : mode === 'draw'
            ? DrawMode
            : mode === 'trivia'
              ? TriviaMode
              : ClassicMode;
  const modeEl = <ModeComp key={`${mode}-${endless}`} endless={endless} />;

  return (
    <div className="app">
      <div className="topbar-right">
        <LangSwitcher lang={lang} mode={mode} />
        <AccountChip />
      </div>
      <header className="hero">
        <Link className="brand" to={buildPath(lang, 'classic')}>
          <img className="brand-emblem" src="/icon-112.png" alt="" width={52} height={52} />
          <span className="brandname">
            Geo<span className="dle">Dle</span>
          </span>
        </Link>
        {view === 'game' && (
          <h1 className="tagline">
            {meta.h1}
            <span className="subtitle2">{mode === 'connections' ? t.conn.subtitle : mode === 'trivia' ? t.trivia.subtitle : t.subtitle}</span>
          </h1>
        )}
      </header>

      <div className="content">
        {view === 'game' ? (
          <>
            <nav className="mode-nav" aria-label="Game modes">
              {/* Trivia 只有每日一套题,无尽模式下不给它入口(否则是条通往不存在页面的链接) */}
              {MODE_DEFS.filter(({ key }) => !(endless && key === 'trivia')).map(({ key, Icon }) => {
                const isDone = !endless && solved.includes(key);
                return (
                  <Link
                    key={key}
                    className={`mode-btn${mode === key ? ' active' : ''}${isDone ? ' done' : ''}`}
                    to={buildPath(lang, key, endless)}
                  >
                    <span className="mi">
                      <Icon />
                      {isDone && <IconCheck className="chk" width={12} height={12} />}
                    </span>
                    <span>{t.modes[key]}</span>
                  </Link>
                );
              })}
            </nav>

            {mode !== 'connections' && mode !== 'trivia' && <ModeToggle lang={lang} mode={mode} endless={endless} />}

            <div
              className={`mode-panel mode-${mode}${switching ? ' switching' : ''}`}
              onAnimationEnd={(e) => {
                if (e.target === e.currentTarget) setSwitching(false);
              }}
            >
              <main className="game-main">{modeEl}</main>

              {/* 战绩条/无尽统计 + 倒计时排在游戏区之后:先看到题目和棋盘,战绩沉到下方 */}
              {endless ? (
                <EndlessBar mode={mode} />
              ) : mode !== 'connections' && mode !== 'trivia' ? (
                <>
                  <DailyStrip lang={lang} mode={mode} solved={solved} streak={streak} />
                  <Countdown />
                </>
              ) : null}
            </div>

            <NativeBanner key={mode} />

            <div className="home-lb">
              <InView minHeight={460}>
                <Suspense fallback={<LeaderboardSkeleton />}>
                  <Leaderboard embedded mode={mode} />
                </Suspense>
              </InView>
            </div>

            <Link className="countries-promo" to={buildCountries(lang)}>
              <span className="cp-icon" aria-hidden="true">
                <IconGlobe />
              </span>
              <span className="cp-text">
                <b>{t.country.promoTitle}</b>
                <span>{t.country.promoDesc}</span>
              </span>
              <span className="cp-cta">{t.country.promoCta} →</span>
            </Link>

            <RecommendedGames lang={lang} />

            <SeoContent lang={lang} mode={mode} honors={mode === 'classic' && !endless} />
          </>
        ) : (
          <Suspense fallback={view === 'profile' ? <ProfileSkeleton /> : <LeaderboardSkeleton />}>
            {view === 'country' ? (
              <CountryPage lang={lang} slug={slug || ''} />
            ) : view === 'countries' ? (
              <CountriesPage />
            ) : view === 'leaderboard' ? (
              <Leaderboard />
            ) : view === 'profile' ? (
              <PublicProfile id={userId || ''} />
            ) : view === 'terms' ? (
              <Terms />
            ) : view === 'settings' ? (
              <Settings />
            ) : (
              <Privacy />
            )}
          </Suspense>
        )}

        <nav className="site-nav" aria-label="Site">
          <Link to={buildCountries(lang)}>{t.country.listTitle}</Link>
          <span aria-hidden="true">·</span>
          <Link to={buildLeaderboard(lang)}>{t.lb.nav}</Link>
          <span aria-hidden="true">·</span>
          <Link to={buildPrivacy(lang)}>{t.privacy.title}</Link>
          <span aria-hidden="true">·</span>
          <Link to={buildTerms(lang)}>{t.terms.title}</Link>
        </nav>

        <footer className="site-footer">{t.footer}</footer>
      </div>

      <div className="frame-corners" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

export default function App() {
  const { lang, mode, endless, view, userId, slug } = useRoute();
  return (
    <I18nProvider lang={lang}>
      <AccountProvider>
        <Shell lang={lang} mode={mode} endless={endless} view={view} userId={userId} slug={slug} />
      </AccountProvider>
    </I18nProvider>
  );
}
